import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  IconButton,
  Stack,
  Chip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Item, Room } from '../types';
import { cacheService } from '../../functions/services/cacheService';

interface ItemListProps {
  selectedRooms: string[];
  rooms: Room[];
  onItemsChange: (items: { itemId: string; quantity: number; roomId: string; isCustom?: boolean; unitPrice?: number }[]) => void;
}

const ItemList: React.FC<ItemListProps> = ({ selectedRooms, rooms, onItemsChange }) => {
  const theme = useTheme();
  const [items, setItems] = useState<Record<string, Item[]>>({});
  const [customItems, setCustomItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newItemInputs, setNewItemInputs] = useState<Record<string, { name: string; quantity: number; unitPrice: number }>>({});
  const [selectedAdditionalItems, setSelectedAdditionalItems] = useState<Record<string, { itemId: string; quantity: number; unitPrice: number; description?: string }>>({});
  const [editingPrice, setEditingPrice] = useState<{ itemId: string; roomId: string; value: number } | null>(null);

  useEffect(() => {
    const loadItems = async () => {
      setLoading(true);
      try {
        // Get room items from cache for initial items
        const roomItems = cacheService.getRoomItems();
        console.log('ItemList: Loaded room items:', roomItems);
        
        // Get all items for additional items selection
        const allItems = cacheService.getItems();
        console.log('ItemList: Loaded all items:', allItems);
        
        // Group room items by room
        const itemsByRoom: Record<string, Item[]> = {};
        selectedRooms.forEach(roomId => {
          // Get room items for this room
          const roomItemsForRoom = roomItems
            .filter(roomItem => roomItem.roomId === roomId)
            .map(roomItem => {
              // Find the corresponding item details
              const itemDetails = allItems.find(item => item.id === roomItem.itemId);
              if (!itemDetails) return null;
              return {
                ...itemDetails,
                quantity: roomItem.quantity || 1,
                roomId: roomId,
                isCustom: false
              } as Item;
            })
            .filter((item): item is Item => item !== null);
          
          itemsByRoom[roomId] = roomItemsForRoom;
        });
        
        setItems(itemsByRoom);

        // Pass initial items to parent only if we have items
        const initialItems = Object.entries(itemsByRoom).flatMap(([roomId, roomItems]) =>
          roomItems.map(item => ({
            itemId: item.id,
            roomId,
            quantity: item.quantity || 0,
            unitPrice: item.unitPrice || 0,
            isCustom: false
          }))
        );

        if (initialItems.length > 0) {
          console.log('ItemList: Sending initial items to parent:', initialItems);
          onItemsChange(initialItems);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading items:', err);
        setError('Failed to load items');
        setLoading(false);
      }
    };

    if (selectedRooms.length > 0) {
      loadItems();
    }
  }, [selectedRooms]);

  const handleItemChange = (itemId: string, roomId: string, quantity: number, unitPrice?: number) => {
    console.log('handleItemChange called with:', { itemId, roomId, quantity, unitPrice });
    
    // Update local state and collect items in one go
    setItems(prev => {
      const updated = {
        ...prev,
        [roomId]: prev[roomId].map(item => 
          item.id === itemId ? { ...item, quantity, unitPrice: unitPrice ?? item.unitPrice } : item
        )
      };
      console.log('Updated items state:', updated);

      // Collect all items from all rooms with their current values
      const allItems = Object.entries(updated).flatMap(([roomId, roomItems]) => 
        roomItems.map(item => ({
          itemId: item.id,
          roomId,
          quantity: item.quantity || 0,
          unitPrice: item.unitPrice || 0,
          isCustom: customItems.some(customItem => customItem.id === item.id)
        }))
      );

      // Add custom items
      const customItemsList = customItems.map(item => ({
        itemId: item.id,
        roomId: item.roomId || '',
        quantity: item.quantity || 0,
        unitPrice: item.unitPrice || 0,
        isCustom: true
      }));

      // Send all items to parent without filtering
      const finalItems = [...allItems, ...customItemsList];
      console.log('Sending items to parent:', finalItems);
      onItemsChange(finalItems);

      return updated;
    });

    if (customItems.find(item => item.id === itemId)) {
      setCustomItems(prev => {
        const updated = prev.map(item => 
          item.id === itemId ? { ...item, quantity, unitPrice: unitPrice ?? item.unitPrice } : item
        );
        console.log('Updated custom items:', updated);
        return updated;
      });
    }
  };

  const handlePriceChange = (itemId: string, roomId: string, newPrice: number) => {
    const validatedPrice = Math.max(0, newPrice);
    setEditingPrice(null);

    setItems(prev => ({
      ...prev,
      [roomId]: prev[roomId].map(item => 
        item.id === itemId ? { ...item, unitPrice: validatedPrice } : item
      )
    }));

    if (customItems.find(item => item.id === itemId)) {
      setCustomItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, unitPrice: validatedPrice } : item
      ));
    }

    handleItemChange(itemId, roomId, items[roomId]?.find(i => i.id === itemId)?.quantity || 0, validatedPrice);
  };

  const handleAddCustomItem = () => {
    const selectedItem = selectedAdditionalItems[''];
    if (!selectedItem?.itemId) return;

    const newItem: Item = {
      id: selectedItem.itemId,
      itemId: selectedItem.itemId,
      name: `Custom Item ${customItems.length + 1}`,
      quantity: selectedItem.quantity,
      unitPrice: selectedItem.unitPrice
    };

    setCustomItems(prev => [...prev, newItem]);
    handleItemChange(selectedItem.itemId, '', selectedItem.quantity, selectedItem.unitPrice);
    setSelectedAdditionalItems({});
  };

  const handleRemoveCustomItem = (itemId: string) => {
    setCustomItems(prev => prev.filter(item => item.id !== itemId));
    handleItemChange(itemId, '', 0);
  };

  const handleRemoveItem = (itemId: string, roomId: string) => {
    setItems(prev => ({
      ...prev,
      [roomId]: prev[roomId].filter(item => item.id !== itemId)
    }));
    handleItemChange(itemId, roomId, 0);
  };

  const handleNewItemInputChange = (roomId: string, field: 'name' | 'quantity' | 'unitPrice', value: string | number) => {
    setNewItemInputs(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [field]: value
      }
    }));
  };

  const handleAddAdditionalItem = (roomId: string) => {
    const selectedItem = selectedAdditionalItems[roomId];
    if (!selectedItem?.itemId) return;

    // Find the selected item from available items
    const availableItems = getAvailableItems(roomId);
    const itemToAdd = availableItems.find(item => item.id === selectedItem.itemId);
    
    if (itemToAdd) {
      // Update local state
      setItems(prev => ({
        ...prev,
        [roomId]: [...(prev[roomId] || []), {
          ...itemToAdd,
          quantity: selectedItem.quantity || 1,
          unitPrice: selectedItem.unitPrice || itemToAdd.unitPrice,
          description: selectedItem.description || itemToAdd.description
        }]
      }));

      // Update parent component
      handleItemChange(
        selectedItem.itemId,
        roomId,
        selectedItem.quantity || 1,
        selectedItem.unitPrice || itemToAdd.unitPrice
      );

      // Reset the form
      setSelectedAdditionalItems(prev => ({
        ...prev,
        [roomId]: { itemId: '', quantity: 0, unitPrice: 0, description: '' }
      }));
    }
  };

  const handleAdditionalItemChange = (roomId: string, field: 'itemId' | 'quantity' | 'unitPrice' | 'description', value: string | number) => {
    setSelectedAdditionalItems(prev => ({
      ...prev,
      [roomId]: { ...prev[roomId], [field]: value }
    }));
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <Typography variant="h6" color="text.secondary">Chargement des items...</Typography>
    </Box>
  );
  
  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <Typography variant="h6" color="error">{error}</Typography>
    </Box>
  );
  
  if (selectedRooms.length === 0) return null;

  // Get all available items for each room
  const getAvailableItems = (roomId: string) => {
    const currentRoomItems = items[roomId] || [];
    const currentRoomItemIds = new Set(currentRoomItems.map(item => item.id));
    
    // Get all items from cache that aren't already in the room
    return cacheService.getItems()
      .filter(item => !currentRoomItemIds.has(item.id));
  };

  return (
    <Box sx={{ mt: 4 }}>
      {selectedRooms.map(roomId => {
        const room = rooms.find(r => r.id === roomId);
        const roomItems = items[roomId] || [];
        const roomCustomItems = customItems.filter(item => item.roomId === roomId);
        const availableItems = getAvailableItems(roomId);
        const selectedItem = availableItems.find(item => item.id === selectedAdditionalItems[roomId]?.itemId);

        return (
          <Paper 
            key={roomId} 
            elevation={2}
            sx={{ 
              p: 4, 
              mb: 4,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {room?.name || roomId}
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      backgroundColor: theme.palette.primary.light, 
                      color: theme.palette.primary.contrastText,
                      fontSize: '1rem',
                      py: 2
                    }}>Item</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      backgroundColor: theme.palette.primary.light, 
                      color: theme.palette.primary.contrastText,
                      fontSize: '1rem',
                      py: 2
                    }}>Description</TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 600, 
                      backgroundColor: theme.palette.primary.light, 
                      color: theme.palette.primary.contrastText,
                      fontSize: '1rem',
                      py: 2
                    }}>Prix Unitaire</TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 600, 
                      backgroundColor: theme.palette.primary.light, 
                      color: theme.palette.primary.contrastText,
                      fontSize: '1rem',
                      py: 2
                    }}>Quantité</TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 600, 
                      backgroundColor: theme.palette.primary.light, 
                      color: theme.palette.primary.contrastText,
                      fontSize: '1rem',
                      py: 2
                    }}>Total</TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 600, 
                      backgroundColor: theme.palette.primary.light, 
                      color: theme.palette.primary.contrastText,
                      fontSize: '1rem',
                      py: 2
                    }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...roomItems, ...roomCustomItems]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(item => {
                    const quantity = item.quantity || 0;
                    const unitPrice = item.unitPrice || 0;
                    const total = quantity * unitPrice;
                    const isEditing = editingPrice?.itemId === item.id && editingPrice?.roomId === roomId;

                    return (
                      <TableRow 
                        key={item.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover
                          },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                            {item.isCustom && (
                              <Chip 
                                size="small" 
                                label="Personnalisé" 
                                color="secondary"
                                sx={{ 
                                  height: 24,
                                  '& .MuiChip-label': {
                                    px: 1,
                                    fontSize: '0.75rem'
                                  }
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {('description' in item && item.description) && !item.isCustom && (
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={editingPrice?.value ?? unitPrice}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                setEditingPrice(prev => prev ? { ...prev, value } : null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editingPrice) {
                                  handlePriceChange(item.id, roomId, editingPrice.value);
                                } else if (e.key === 'Escape') {
                                  setEditingPrice(null);
                                }
                              }}
                              onBlur={() => {
                                if (editingPrice) {
                                  handlePriceChange(item.id, roomId, editingPrice.value);
                                }
                              }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { 
                                  min: 0,
                                  step: 0.01,
                                  style: { textAlign: 'right' }
                                }
                              }}
                              sx={{ 
                                width: 120,
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                              <Typography sx={{ fontWeight: 500, fontSize: '1rem' }}>
                                ${unitPrice.toFixed(2)}
                              </Typography>
                              <Tooltip title="Modifier le prix">
                                <IconButton
                                  onClick={() => setEditingPrice({ itemId: item.id, roomId, value: unitPrice })}
                                  sx={{
                                    color: 'primary.main',
                                    padding: '8px',
                                    '&:hover': {
                                      backgroundColor: theme.palette.primary.light,
                                      color: theme.palette.primary.contrastText
                                    }
                                  }}
                                >
                                  <EditIcon style={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newQuantity = Math.max(0, quantity - 1);
                                handleItemChange(item.id, roomId, newQuantity);
                              }}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '4px',
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                            >
                              -
                            </IconButton>
                            <TextField
                              size="small"
                              type="number"
                              value={quantity}
                              onChange={(e) => {
                                const newValue = Math.max(0, parseInt(e.target.value) || 0);
                                handleItemChange(item.id, roomId, newValue);
                              }}
                              onBlur={(e) => {
                                const newValue = Math.max(0, parseInt(e.target.value) || 0);
                                handleItemChange(item.id, roomId, newValue);
                              }}
                              inputProps={{
                                min: 0,
                                style: { 
                                  textAlign: 'center',
                                  width: '60px',
                                  padding: '8px'
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'divider'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main'
                                  }
                                }
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newQuantity = quantity + 1;
                                handleItemChange(item.id, roomId, newQuantity);
                              }}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '4px',
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover
                                }
                              }}
                            >
                              +
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1rem' }}>
                            ${total.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Supprimer l'item">
                              <IconButton
                                onClick={() => item.isCustom ? handleRemoveCustomItem(item.id) : handleRemoveItem(item.id, roomId)}
                                color="error"
                                sx={{
                                  '&:hover': {
                                    backgroundColor: theme.palette.error.light,
                                    color: theme.palette.error.contrastText
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Additional Item Row */}
                  {availableItems.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <FormControl fullWidth>
                          <Select
                            value={selectedAdditionalItems[roomId]?.itemId || ''}
                            onChange={(e) => handleAdditionalItemChange(roomId, 'itemId', e.target.value)}
                            displayEmpty
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main'
                              }
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Sélectionner un item</em>
                            </MenuItem>
                            {availableItems
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          onClick={() => handleAddAdditionalItem(roomId)}
                          disabled={!selectedAdditionalItems[roomId]?.itemId}
                          startIcon={<AddIcon />}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 120,
                            py: 1,
                            px: 2,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }}
                        >
                          Ajouter
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
    </Box>
  );
};

export default ItemList; 